import { ConfirmProvider, Toaster, TooltipProvider } from '@servicetim/ui/components';
import { AuthGate } from '@servicetim/ui/lib';
import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import type { ComponentType } from 'react';
import { AppShell } from '@/components/layout/app-shell';
import { RequireTenantRole } from '@/components/layout/not-allowed';
import { NotFoundPage } from './pages/system/not-found';
import { validateAutomationsSearch, validateProjectsSearch, validateViewSearch } from './search';

/**
 * Code-based route tree (no file-based routing, no codegen). Page components are lazy so each
 * area (`routes/pages/<area>/`) becomes its own chunk (see `vite.config.ts` manualChunks).
 *
 *   /                            dashboard                       ─┐
 *   /queue /board /tickets/$idOrKey /jobs   agent surfaces         ├─ "app" layout: AuthGate + AppShell
 *   /projects … /permission-schemes/$schemeId   configuration      │
 *   /tenant /users … /audit      tenant admin ("admin" gate)       ─┘
 *   /callback, /signed-out       outside the gate
 *
 * Adding a page: create `pages/<area>/<name>.tsx` exporting `<Name>Page`, add a `createRoute`
 * below with `lazyRouteComponent(() => import('./pages/<area>/<name>'), '<Name>Page')`, list it
 * in `addChildren`, and (if it is a top-level screen) add a `nav.ts` entry.
 */

function RootLayout() {
  return <Outlet />;
}

function AppLayout() {
  return (
    <AuthGate>
      <TooltipProvider delayDuration={300}>
        <ConfirmProvider>
          <AppShell>
            <Outlet />
          </AppShell>
          <Toaster />
        </ConfirmProvider>
      </TooltipProvider>
    </AuthGate>
  );
}

/** Pathless gate: tenant admin (or owner) only. Lower roles see the not-allowed state. */
function AdminGateLayout() {
  return (
    <RequireTenantRole minimum="admin">
      <Outlet />
    </RequireTenantRole>
  );
}

const rootRoute = createRootRoute({ component: RootLayout });

/** Pathless layout route: contributes <AuthGate> + <AppShell> without a URL segment. */
const appRoute = createRoute({ getParentRoute: () => rootRoute, id: 'app', component: AppLayout });

/** Pathless admin gate under the app layout. */
const adminRoute = createRoute({
  getParentRoute: () => appRoute,
  id: 'admin',
  component: AdminGateLayout,
});

/* ----------------------------- system ----------------------------- */

const indexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  component: lazyRouteComponent(() => import('./pages/system/dashboard'), 'DashboardPage'),
});
const automationsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/automations',
  validateSearch: validateAutomationsSearch,
  component: lazyRouteComponent(() => import('./pages/system/automations'), 'AutomationsPage'),
});
const importsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/imports',
  component: lazyRouteComponent(() => import('./pages/system/imports'), 'ImportsPage'),
});
const importDetailRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/imports/$importId',
  component: lazyRouteComponent(() => import('./pages/system/import-detail'), 'ImportDetailPage'),
});

/* ----------------------------- tenant (admin only) ----------------------------- */

const tenantRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/tenant',
  component: lazyRouteComponent(() => import('./pages/tenant/tenant'), 'TenantSettingsPage'),
});
const usersRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/users',
  component: lazyRouteComponent(() => import('./pages/tenant/users'), 'UsersPage'),
});
const groupsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/groups',
  component: lazyRouteComponent(() => import('./pages/tenant/groups'), 'GroupsPage'),
});
const organizationsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/organizations',
  component: lazyRouteComponent(() => import('./pages/tenant/organizations'), 'OrganizationsPage'),
});
const tenantFieldsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/fields',
  component: lazyRouteComponent(() => import('./pages/tenant/fields'), 'TenantFieldsPage'),
});
const tenantRolesRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/roles',
  component: lazyRouteComponent(() => import('./pages/tenant/roles'), 'TenantRolesPage'),
});
const apiKeysRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/api-keys',
  component: lazyRouteComponent(() => import('./pages/tenant/api-keys'), 'ApiKeysPage'),
});
const webhooksRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/webhooks',
  component: lazyRouteComponent(() => import('./pages/tenant/webhooks'), 'WebhooksPage'),
});
const linkTypesRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/link-types',
  component: lazyRouteComponent(() => import('./pages/tenant/link-types'), 'LinkTypesPage'),
});
const auditRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/audit',
  component: lazyRouteComponent(() => import('./pages/tenant/audit'), 'AuditLogPage'),
});

/* ----------------------------- projects ----------------------------- */

const projectsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/projects',
  validateSearch: validateProjectsSearch,
  component: lazyRouteComponent(() => import('./pages/projects/projects'), 'ProjectsPage'),
});
const projectNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/projects/new',
  component: lazyRouteComponent(() => import('./pages/projects/project-new'), 'ProjectNewPage'),
});
const templatesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/templates',
  component: lazyRouteComponent(() => import('./pages/projects/templates'), 'TemplatesPage'),
});

/** Layout with the project header + tabs; children render in its <Outlet>. */
const projectRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/projects/$projectId',
  component: lazyRouteComponent(() => import('./pages/projects/project-layout'), 'ProjectLayout'),
});
const projectIndexRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/',
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/projects/$projectId/settings', params, replace: true });
  },
});
const projectTab = <TPath extends string, TName extends string>(
  path: TPath,
  importer: () => Promise<Record<TName, ComponentType>>,
  name: TName,
) =>
  createRoute({
    getParentRoute: () => projectRoute,
    path,
    component: lazyRouteComponent(importer, name),
  });

const projectSettingsRoute = projectTab(
  'settings',
  () => import('./pages/projects/project-settings'),
  'ProjectSettingsPage',
);
const projectIssueTypesRoute = projectTab(
  'issue-types',
  () => import('./pages/projects/project-issue-types'),
  'ProjectIssueTypesPage',
);
const projectPrioritiesRoute = projectTab(
  'priorities',
  () => import('./pages/projects/project-priorities'),
  'ProjectPrioritiesPage',
);
const projectFieldsRoute = projectTab(
  'fields',
  () => import('./pages/projects/project-fields'),
  'ProjectFieldsPage',
);
const projectFormsRoute = projectTab(
  'forms',
  () => import('./pages/projects/project-forms'),
  'ProjectFormsPage',
);
const projectWorkflowsRoute = projectTab(
  'workflows',
  () => import('./pages/projects/project-workflows'),
  'ProjectWorkflowsPage',
);
const projectRolesRoute = projectTab(
  'roles',
  () => import('./pages/projects/project-roles'),
  'ProjectRolesPage',
);
const projectSchemeRoute = projectTab(
  'scheme',
  () => import('./pages/projects/project-scheme'),
  'ProjectSchemePage',
);
const projectViewsRoute = projectTab(
  'views',
  () => import('./pages/projects/project-views'),
  'ProjectViewsPage',
);
const projectSlaRoute = projectTab(
  'sla',
  () => import('./pages/projects/project-sla'),
  'ProjectSlaPage',
);
const projectCannedRoute = projectTab(
  'canned',
  () => import('./pages/projects/project-canned'),
  'ProjectCannedPage',
);
const projectAutomationsRoute = projectTab(
  'automations',
  () => import('./pages/projects/project-automations'),
  'ProjectAutomationsPage',
);
const workflowEditorRoute = projectTab(
  'workflows/$workflowId',
  () => import('./pages/projects/workflow-editor'),
  'WorkflowEditorPage',
);
const formEditorRoute = projectTab(
  'forms/$formId',
  () => import('./pages/projects/form-editor'),
  'FormEditorPage',
);

/* ----------------------------- access ----------------------------- */

const permissionSchemesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/permission-schemes',
  component: lazyRouteComponent(
    () => import('./pages/access/permission-schemes'),
    'PermissionSchemesPage',
  ),
});
const permissionSchemeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/permission-schemes/$schemeId',
  component: lazyRouteComponent(
    () => import('./pages/access/permission-scheme'),
    'PermissionSchemePage',
  ),
});

/* ----------------------------- agent surfaces ----------------------------- */

const queueRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/queue',
  validateSearch: validateViewSearch,
  component: lazyRouteComponent(() => import('./pages/agent/queue'), 'QueuePage'),
});
const boardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/board',
  validateSearch: validateViewSearch,
  component: lazyRouteComponent(() => import('./pages/agent/board'), 'BoardPage'),
});
const ticketRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/tickets/$idOrKey',
  component: lazyRouteComponent(() => import('./pages/agent/ticket'), 'TicketPage'),
});
const jobsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/jobs',
  component: lazyRouteComponent(() => import('./pages/agent/jobs'), 'JobsPage'),
});
const approvalsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/approvals',
  component: lazyRouteComponent(() => import('./pages/agent/approvals'), 'ApprovalsPage'),
});
const notificationsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/notifications',
  component: lazyRouteComponent(() => import('./pages/agent/notifications'), 'NotificationsPage'),
});
const notificationSettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/notifications',
  component: lazyRouteComponent(
    () => import('./pages/agent/notification-settings'),
    'NotificationSettingsPage',
  ),
});

/* ----------------------------- outside the gate ----------------------------- */

const callbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/callback',
  component: lazyRouteComponent(() => import('./pages/system/callback'), 'CallbackPage'),
});
const signedOutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signed-out',
  component: lazyRouteComponent(() => import('./pages/system/signed-out'), 'SignedOutPage'),
});

const routeTree = rootRoute.addChildren([
  appRoute.addChildren([
    indexRoute,
    adminRoute.addChildren([
      tenantRoute,
      usersRoute,
      groupsRoute,
      organizationsRoute,
      tenantFieldsRoute,
      tenantRolesRoute,
      apiKeysRoute,
      webhooksRoute,
      linkTypesRoute,
      auditRoute,
      automationsRoute,
      importsRoute,
      importDetailRoute,
    ]),
    projectsRoute,
    projectNewRoute,
    templatesRoute,
    projectRoute.addChildren([
      projectIndexRoute,
      projectSettingsRoute,
      projectIssueTypesRoute,
      projectPrioritiesRoute,
      projectFieldsRoute,
      projectFormsRoute,
      projectWorkflowsRoute,
      projectRolesRoute,
      projectSchemeRoute,
      projectViewsRoute,
      projectSlaRoute,
      projectCannedRoute,
      projectAutomationsRoute,
      workflowEditorRoute,
      formEditorRoute,
    ]),
    permissionSchemesRoute,
    permissionSchemeRoute,
    queueRoute,
    boardRoute,
    ticketRoute,
    jobsRoute,
    approvalsRoute,
    notificationsRoute,
    notificationSettingsRoute,
  ]),
  callbackRoute,
  signedOutRoute,
]);

export function createAppRouter() {
  return createRouter({
    routeTree,
    defaultPreload: 'intent',
    defaultNotFoundComponent: NotFoundPage,
    scrollRestoration: true,
  });
}

export type AppRouter = ReturnType<typeof createAppRouter>;

declare module '@tanstack/react-router' {
  interface Register {
    router: AppRouter;
  }
}

/** Route objects screens may need for `useParams` / `useSearch` with a `from`. */
export const routes = {
  app: appRoute,
  project: projectRoute,
  queue: queueRoute,
  board: boardRoute,
  ticket: ticketRoute,
  permissionScheme: permissionSchemeRoute,
  workflowEditor: workflowEditorRoute,
  formEditor: formEditorRoute,
};
